import React from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { Box, Paper, Typography, useTheme, alpha, Grid } from '@mui/material';

ChartJS.register(ArcElement, Tooltip, Legend);

interface OwnerDistributionChartProps {
  data: {
    labels: string[];
    datasets: {
      data: number[];
      backgroundColor: string[];
      borderColor: string[];
      borderWidth: number;
    }[];
  };
  totalOwners: number;
}

const OwnerDistributionChart: React.FC<OwnerDistributionChartProps> = ({ data, totalOwners }) => {
  const theme = useTheme();

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          font: {
            family: theme.typography.fontFamily,
            size: 12
          },
          usePointStyle: true,
          padding: 20,
          generateLabels: function(chart: any) {
            // Use standard labels generation but with custom text
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const labels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
            
            return data.labels.map((label, i) => {
              const count = data.datasets[0].data[i];
              const percentage = Math.round((count / totalOwners) * 100);
              
              return {
                text: `${label} (${count} - ${percentage}%)`,
                fillStyle: data.datasets[0].backgroundColor[i],
                strokeStyle: data.datasets[0].borderColor[i],
                lineWidth: data.datasets[0].borderWidth,
                hidden: false,
                index: i,
                datasetIndex: 0
              };
            });
          }
        }
      },
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.raw || 0;
            const percentage = Math.round((value / totalOwners) * 100);
            return `${label}: ${value} владельцев (${percentage}%)`;
          }
        }
      }
    },
    animation: {
      animateScale: true,
      animateRotate: true
    },
    hover: {
      mode: 'nearest' as const,
      intersect: true
    }
  };

  return (
    <Paper 
      sx={{ 
        p: 3, 
        height: 400, 
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        transition: 'box-shadow 0.3s ease',
        '&:hover': {
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            Распределение владельцев
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {totalOwners.toLocaleString()} владельцев всего
          </Typography>
        </Grid>
        
        <Grid item xs={12}>
          <Box sx={{ 
            height: 300, 
            width: '100%', 
            position: 'relative',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <Doughnut options={options} data={data} />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default OwnerDistributionChart; 